import {
  Button as MuiButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { spacing } from '@material-ui/system'
import { AxiosError } from 'axios'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  Appointment,
  ASN,
  Driver,
  Facility,
  HoursOfOperation,
  DaySpec,
  Truckload,
} from 'src/api/models'
import { useQueryPaginator } from 'src/components/Table'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import { useForm, DateTimeField, MapSelectField, TextareaField, QuerySelectField } from 'src/forms'
import { MiniScheduler } from 'src/components/Calendar'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import { DriverDialog } from 'src/modules/supply_chain/components/DriverDialog'

import styled from 'styled-components'
import * as yup from 'yup'

const Button = styled(MuiButton)(spacing)

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

const minutesToTimeStr = (minutes: number): string => {
  const h = Math.floor(minutes / 60)
    .toString()
    .padStart(2, '0')
  const m = (minutes % 60).toString().padStart(2, '0')
  return `${h}:${m}`
}

const getHoursForDate = (
  date: moment.Moment | undefined,
  hours?: HoursOfOperation
): DaySpec | undefined => {
  if (!hours || !date) return undefined
  const dayKey = DAY_KEYS[date.day()]
  const daySpec = hours[dayKey] ?? hours.default
  if (!daySpec || daySpec.open === daySpec.close) return undefined
  return daySpec
}

const useStyles = makeStyles((theme) => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  directionGroup: {
    marginBottom: theme.spacing(1),
  },
  sectionLabel: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
}))

interface AppointmentProps {
  client?: string
  facility: string
  carrier: string
  tracking_number: string // eslint-disable-line camelcase
  status: string
}

const oneOfRequired = {
  asn: 'ASN',
  truckload: 'Truckload',
}

const appointmentSchema = yup.object().shape({
  asn: yup.string().asn().optional().oneRequired(oneOfRequired),
  truckload: yup.string().truckload().optional().oneRequired(oneOfRequired),
  broker: yup.string().carrier().optional(),
  appointment_type: yup.string().required().oneOf(Object.keys(Appointment.AppointmentType)),
  start: yup.date().required(),
  end: yup.date().required(),
  dock: yup.string().dock().optional(),
  driver: yup.string().driver().optional(),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: () => void
  update?: boolean
  appointment?: Appointment
  asn?: ASN
  truckload?: Truckload
  facility?: Facility
  initialStart?: moment.Moment
  initialEnd?: moment.Moment
  hoursOfOperation?: HoursOfOperation
}

export const AppointmentDialog = (props: Props) => {
  const classes = useStyles()
  const {
    open,
    handleClose,
    onSave,
    update = false,
    appointment,
    asn: initialASN,
    truckload: initialTruckload,
    facility: initialFacility,
    initialStart,
    initialEnd,
    hoursOfOperation,
  } = props

  const [saving, setSaving] = useState(false)

  const [asn, setASN] = useState(appointment?.asn || initialASN)
  const [truckload, setTruckload] = useState(appointment?.truckload || initialTruckload)

  // Direction: inbound uses ASN, outbound uses Truckload
  const directionLocked = !!(update || initialASN || initialTruckload)
  const [direction, setDirection] = useState<'asn' | 'truckload'>(() =>
    appointment?.truckload || initialTruckload ? 'truckload' : 'asn'
  )

  const facility = initialFacility || asn?.facility || truckload?.facility
  const asnPaginator = useQueryPaginator('asn-list', skuit.sc.asn.list, {
    params: { open: true, unscheduled: true },
  })
  const carrierPaginator = useQueryPaginator('carrier-list', skuit.sc.carriers.list, {
    params: { open: true, unscheduled: true },
  })
  const dockPaginator = useQueryPaginator('dock-list', skuit.sc.docks.list, {
    params: { facilities: facility?.guid },
  })
  const driverPaginator = useQueryPaginator('driver-list', skuit.sc.drivers.list)
  const truckloadPaginator = useQueryPaginator('truckload-list', skuit.sc.freight.truckloads.list, {
    params: { open: true, unscheduled: true },
  })

  const { Form, data: formData } = useForm(appointmentSchema)

  const asnPatchFilters = asnPaginator.patchFilters
  const dockPatchFilters = dockPaginator.patchFilters
  const truckloadPatchFilters = truckloadPaginator.patchFilters
  useEffect(() => {
    asnPatchFilters({ facilities: facility?.guid })
    dockPatchFilters({ facilities: facility?.guid })
    truckloadPatchFilters({ facilities: facility?.guid })
  }, [asnPatchFilters, dockPatchFilters, truckloadPatchFilters, facility])

  // Clear the hidden field when direction changes
  useEffect(() => {
    if (directionLocked) return
    if (direction === 'asn') {
      formData.setValue('truckload', undefined)
      setTruckload(undefined)
    } else {
      formData.setValue('asn', undefined)
      setASN(undefined)
    }
  }, [direction]) // eslint-disable-line

  const onSubmit = (data: AppointmentProps) => {
    setSaving(true)
    let func = skuit.sc.appointments.create
    let action = 'creat'
    if (update) {
      func = (data: AppointmentProps) => skuit.sc.appointments.patch(appointment?.guid || '', data)
      action = 'updat'
    }

    func(data)
      .then((newAppointment: Appointment) => {
        handleClose()
        if (onSave) {
          onSave()
        }
        toast.success(() => (
          <Notification
            message={`Successfully ${action}ed Appointment: ${newAppointment.name}`}
            newObject={newAppointment}
          />
        ))
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Appointment')
        setApiErrors(formData.setError, error)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  const startDefault = initialStart || appointment?.start
  const endDefault = initialEnd || appointment?.end

  const [selectedStartDate, setSelectedStartDate] = useState<moment.Moment | undefined>(
    startDefault
  )
  const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | undefined>(endDefault)

  const startHours = getHoursForDate(selectedStartDate, hoursOfOperation)
  const endHours = getHoursForDate(selectedEndDate, hoursOfOperation)

  const onSchedulerChange = (newStart: moment.Moment, newEnd: moment.Moment) => {
    formData.setValue('start', newStart)
    formData.setValue('end', newEnd)
    setSelectedStartDate(newStart)
    setSelectedEndDate(newEnd)
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
          <DialogTitle>{update ? 'Edit' : 'New'} Appointment</DialogTitle>
          <DialogContent>
            {/* Direction toggle */}
            {!directionLocked && (
              <RadioGroup
                row
                value={direction}
                onChange={(e) => setDirection(e.target.value as 'asn' | 'truckload')}
                className={classes.directionGroup}
              >
                <FormControlLabel
                  value="asn"
                  control={<Radio color="primary" size="small" />}
                  label="Inbound (ASN)"
                />
                <FormControlLabel
                  value="truckload"
                  control={<Radio color="primary" size="small" />}
                  label="Outbound (Truckload)"
                />
              </RadioGroup>
            )}

            {/* ASN or Truckload */}
            {direction === 'asn' ? (
              <QuerySelectField
                name="asn"
                label="ASN"
                paginator={asnPaginator}
                defaultValue={asn}
                disabled={!!appointment || !!initialASN}
                onSelected={(a: ASN) => setASN(a)}
                getOptionLabel={(a: ASN) =>
                  a ? [a.name, a.client?.name].filter(Boolean).join(' · ') : ''
                }
              />
            ) : (
              <QuerySelectField
                name="truckload"
                label="Truckload"
                paginator={truckloadPaginator}
                defaultValue={truckload}
                disabled={!!appointment || !!initialTruckload}
                onSelected={(t: Truckload) => setTruckload(t)}
                getOptionLabel={(t: Truckload) =>
                  t ? [t.name, t.client?.name].filter(Boolean).join(' · ') : ''
                }
              />
            )}

            <MapSelectField
              name="appointment_type"
              label="Appointment Type"
              options={Appointment.AppointmentType}
              defaultValue={appointment?.appointmentType}
            />

            <MiniScheduler
              start={selectedStartDate}
              end={selectedEndDate}
              onChange={onSchedulerChange}
              hoursOfOperation={hoursOfOperation}
              facilityGuid={facility?.guid}
              excludeGuid={appointment?.guid}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DateTimeField
                  name="start"
                  label="Start"
                  defaultValue={startDefault}
                  minTime={startHours ? minutesToTimeStr(startHours.open) : undefined}
                  maxTime={startHours ? minutesToTimeStr(startHours.close) : undefined}
                  onChange={setSelectedStartDate}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateTimeField
                  name="end"
                  label="End"
                  defaultValue={endDefault}
                  minTime={endHours ? minutesToTimeStr(endHours.open) : undefined}
                  maxTime={endHours ? minutesToTimeStr(endHours.close) : undefined}
                  onChange={setSelectedEndDate}
                />
              </Grid>
            </Grid>

            {/* Optional fields */}
            <Typography variant="caption" className={classes.sectionLabel}>
              Optional
            </Typography>
            <Divider />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <QuerySelectField
                  name="broker"
                  label="Broker"
                  paginator={carrierPaginator}
                  defaultValue={appointment?.broker}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <QuerySelectField
                  name="dock"
                  label="Dock Door"
                  paginator={dockPaginator}
                  defaultValue={appointment?.dock}
                />
              </Grid>
            </Grid>

            <QuerySelectField
              name="driver"
              label="Driver"
              paginator={driverPaginator}
              defaultValue={appointment?.driver}
              CreateDialog={DriverDialog}
              createOnSave={(driver: Driver) => {
                driverPaginator.refresh()
                formData.setValue('driver', driver)
              }}
            />
            <TextareaField
              name="internal_notes"
              label="Internal Notes"
              defaultValue={appointment?.internalNotes}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
              Cancel
            </Button>
            <Button type="submit" color="primary" variant="contained" disabled={saving} autoFocus>
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  )
}
